import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import {Button, Col, Container, Row} from 'react-bootstrap';
import {
	ConfirmationDialog
} from "../../Common/ConfirmationDialog";
import {
    GenericModelViewer
} from "../../Common/GenericModelViewer";
import {
    Glyphicon
} from "../../Common/GlyphIcon";
import {useChangePage} from "../../../Hooks/NavigationHooks";
import { ObjectUtils } from "../../../Utils/ObjectUtils";
import { GuidUtils } from "../../../Utils/GuidUtils";
import {
    Survey
} from "../../../Models";
import {service as ConservationActionTypeService} from '../../../Services/ConservationActionTypeService';
import {service as SurveyService} from '../../../Services/SurveyService';
import {service as SurveyTypeService} from '../../../Services/SurveyTypeService';
import {
	useCheckAuth,
} from "../../../Hooks/RouteHooks";
import {
	useMakePageRequest
} from "../../../Hooks/RequestHooks";
import {useContextIndependentPage} from "../../../Hooks/ContextBuilderHooks";
import {useClassNames} from "../../../Hooks/StyleHooks";

import {StreamBankPageHeader} from "../../Common/StreamBankPageHeader";
import {useDefaultErrorHandler} from "../../../Hooks/ErrorHooks";
import {CopySurveyModal} from "./CopySurveyModal";
import {useValidations} from "../../../Hooks/GenericModelHooks";
import {BOUNDARY_CODE, hasStructuralChanges} from "../../../Hooks/SubmissionBoundaryHooks";


let model = new Survey();


export const SurveysViewPage = () => {
    useCheckAuth();
    useContextIndependentPage();
    const defaultErrorHandler = useDefaultErrorHandler();

    const classNames = useClassNames('sb-survey-view-page');
    const makePageRequest = useMakePageRequest();
    const changePage = useChangePage();

    const currentUser = useSelector(state => state.userState.user);

    const [survey, setSurvey] = useState(null);
    const [existingSurvey, setExistingSurvey] = useState(null);
    const [surveyTypes, setSurveyTypes] = useState([]);
    const [conservationActionTypes, setConservationActionTypes] = useState([]);
    const [validations, setValidations, isValid] = useValidations();
    
    const [editMode, setEditMode] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [showCopySurveyModal, setShowCopySurveyModal] = useState(false);
    const [boundaryErrorDialog, setBoundaryErrorDialog] = useState(false);
    const [structuralChangeDialog, setStructuralChangeDialog] = useState(false);

    const {surveyId} = useParams();

    useEffect(() => {
        loadData();
    }, [surveyId]);

    const loadData = () => {
        if(surveyId === "new") {
            makePageRequest(
                () => Promise.all([
                    ConservationActionTypeService.Get(),
                    SurveyTypeService.Get()
                ]),
                ([conservationActionTypes, surveyTypes]) => {
                    setSurvey({
                        surveyTypeId: surveyTypes.result[0].surveyTypeId,
                        conservationActionTypeId: conservationActionTypes.result[0].conservationActionTypeId,
                        published: false,
                        organizationId: currentUser?.organizationId,
                    });
                    setSurveyTypes(surveyTypes.result);
                    setConservationActionTypes(conservationActionTypes.result);
                    setEditMode(true);
                }
            );
        } else {
            makePageRequest(
                () => Promise.all([
                    SurveyService.Get(surveyId),
                    ConservationActionTypeService.Get(),
                    SurveyTypeService.Get()
                ]),
                ([survey, conservationActionTypes, surveyTypes]) => {
                    setSurvey(survey.result);
                    setSurveyTypes(surveyTypes.result);
                    setConservationActionTypes(conservationActionTypes.result);
                    setEditMode(editMode);
                    setExistingSurvey(editMode ? ObjectUtils.Clone(survey) : null);
                }
            );
        }
    };

    // this doesn't work at the moment, but leaving this in for reference
    const getQueryParameterStateDefinitions = () => {
        return [
            {
                stateKey: "editMode",
                parameterName: "edit",
                fromParameterLogic: (x) => { return !!x }
                //defaultValue: false // unspecified to use constructor specified value
            },
        ]
    };

    const enterEditMode = () => {
        setEditMode(true);
        setExistingSurvey(ObjectUtils.Clone(survey));
    };

    const exitEditMode = () => {
        if(existingSurvey) {
            setEditMode(false);
            setSurvey(ObjectUtils.Clone(existingSurvey));
            setExistingSurvey(null);
        } else {
            let newPath = window.location.pathname.replace("/surveys/new", "/surveys");
            changePage(newPath);
        }
    };

    const updateSurvey = useCallback((survey) => setSurvey(survey), []);
    const surveyCustomData = useMemo(() => ({ organizationId: survey?.organizationId }), [survey?.organizationId]);

    const doSave = () => {
        if(survey.surveyId) {
            makePageRequest(
                () => SurveyService.UpdateSurvey(survey),
                (x) => {
                    setEditMode(false);
                    setSurvey(x.result);
                }
            );
        } else {
            makePageRequest(
                () => SurveyService.CreateSurvey(survey),
                (x) => {
                    setEditMode(false);
                    const newPath = window.location.pathname.split('/').slice(0, -1).join('/') + '/' + x.result.surveyId;
                    changePage(newPath);
                },
                (x) => {
                    defaultErrorHandler(x);
                }
            );
        }
    };

    const saveSurvey = () => {
        // Guard: only one customSubmissionBoundary section allowed per survey
        const boundaryCount = (survey?.SurveySections ?? []).filter(s => s.code === BOUNDARY_CODE).length;
        if (boundaryCount > 1) {
            setBoundaryErrorDialog(true);
            return;
        }

        // Warn (non-blocking) when structural changes could affect existing submissions
        if (hasStructuralChanges(survey, existingSurvey)) {
            setStructuralChangeDialog(true);
            return;
        }

        doSave();
    };

    const duplicateSurvey = (surveyTypeId, conservationActionTypeId, organizationId, programId) => {
        setShowCopySurveyModal(false);
        enterEditMode();

        // unset primary keys
        setSurvey({
            ...survey,
            surveyId: null,
            surveyTypeId,
            conservationActionTypeId,
            organizationId,
            SurveyPrograms: programId ? [{
                programId,
                surveyId: null,
                surveyProgramId: null,
                _tempId: GuidUtils.Generate(),
            }] : [],
            SurveySections: survey?.SurveySections?.map(section => ({
                ...section,
                surveyId: null,
                surveySectionId: null,
                _tempId: GuidUtils.Generate(),
                SurveyQuestions: section?.SurveyQuestions?.map(question => ({
                    ...question,
                    surveyQuestionId: null,
                    _tempId: GuidUtils.Generate(),
                })) ?? []
            })) ?? [],
        })
    }

    const deleteSurvey = () => {
        makePageRequest(
            () => SurveyService.DeleteSurvey(survey.surveyId),
            (x) => {
                changePage("/surveys");
            }
        );
    };

    let conservationActionType = null;
    if(conservationActionTypes) {
        conservationActionType = conservationActionTypes.find((x) => x.conservationActionTypeId == survey.conservationActionTypeId);
    }

    let surveyType = null;
    if(surveyTypes) {
        surveyType = surveyTypes.find((x) => x.surveyTypeId == survey.surveyTypeId);
    }

    // don't render anything until we are loaded.
    if(!Boolean(conservationActionTypes.length) || !Boolean(surveyTypes.length) || !Boolean(survey)) {
        return null;
    }

    return <Container className={classNames}>
        <CopySurveyModal
            show={showCopySurveyModal}
            surveyTypes={surveyTypes}
            conservationActionTypes={conservationActionTypes}
            targetSurveyTypeId={surveyType?.surveyTypeId}
            targetConservationActionTypeId={conservationActionType?.conservationActionTypeId}
            targetOrganizationId={survey?.organizationId}
            targetProgramId={survey?.SurveyPrograms?.length ? survey?.SurveyPrograms[0]?.programId : null}
            onHide={ () => setShowCopySurveyModal(false) }
            onConfirm={duplicateSurvey}
        />
        <Row>
            <StreamBankPageHeader
                Title={`Edit ${conservationActionType?.name} ${surveyType?.name ?? ""}`}
            />
            <ConfirmationDialog
                Title="Confirmation"
                Body="Are you sure you want to delete this survey?"
                Display={deleteDialog}
                OnClose={() => setDeleteDialog(false)}
                OnConfirm={() => deleteSurvey()}
            />
            <ConfirmationDialog
                Title="Multiple Submission Boundaries"
                Body="This survey has more than one Submission Boundary section. Only one is allowed. Please remove the duplicate before saving."
                Display={boundaryErrorDialog}
                OnClose={() => setBoundaryErrorDialog(false)}
                OnConfirm={() => setBoundaryErrorDialog(false)}
                ConfirmationButtonMessage="OK"
                CancelButtonMessage={null}
            />
            <ConfirmationDialog
                Title="Structural Change Warning"
                Body={
                    <span>
                        This survey has existing supplier submissions. Changing the number or order of sections, or moving the Submission Boundary, may affect which sections count as required for those submissions. <strong>This cannot be automatically corrected.</strong> Are you sure you want to save?
                    </span>
                }
                Display={structuralChangeDialog}
                OnClose={() => setStructuralChangeDialog(false)}
                OnConfirm={() => { setStructuralChangeDialog(false); doSave(); }}
                ConfirmationButtonMessage="Save Anyway"
            />
            <Col xs={12} md={12}>
                <h2 className="atomic-sectionheader">
                    <div className="float-right btn-edit">
                        {
                            editMode ? [
                                    <Button
                                        variant="save"
                                        key={1}
                                        onClick={() => saveSurvey()}
                                        disabled={!isValid}
                                    >
                                        Save
                                    </Button>,
                                    <Button
                                        variant="cancel"
                                        key={2}
                                        onClick={() => exitEditMode()}
                                    >
                                        Cancel
                                    </Button>,
                                    (surveyId ?? 'new') !== 'new' ?
                                        <Button
                                            variant="delete"
                                            key={4}
                                            onClick={() => setDeleteDialog(true)}
                                        >
                                            Delete
                                        </Button> :
                                        null
                                ] :
                                [
                                    <Button variant="link" onClick={() => setShowCopySurveyModal(true)}>
                                        <Glyphicon glyph="copy">Duplicate</Glyphicon>
                                    </Button>,
                                    <Button variant="link" onClick={() => enterEditMode()}>
                                        <Glyphicon glyph="pencil" />
                                        Edit
                                    </Button>
                                ]
                        }
                    </div>
                    {(survey?.organizationId ? 'Custom' : 'Default')} Survey: {`${conservationActionType?.name} ${surveyType?.name ?? ""}`}
                </h2>

                {
                    survey ?
                        <GenericModelViewer
                            IsNewModel={surveyId === "new"}
                            EditMode={editMode}
                            Data={survey}
                            Update={updateSurvey}
                            OnValidationUpdate={setValidations}
                            Model={model}
                            CustomData={surveyCustomData}
                        />:
                        null
                }
            </Col>
        </Row>
    </Container>
};
